import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import {
  Search,
  MapPin,
  Loader2,
  Crosshair,
  X,
  AlertCircle,
  Pencil,
} from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { cn } from "../lib/utils";

/**
 * PlaceAutocomplete
 *
 * Google-Places-style typeahead. Talks only to the backend (/api/places/*),
 * so the API key stays server-side. Gracefully falls back to a plain-text
 * "manual address" field if the backend reports `configured: false`.
 *
 * Props:
 *  - value:    { name?, address?, latitude?, longitude?, place_id? } | null
 *  - onChange(next)
 *  - accentColor?: string
 *  - placeholder?: string
 *  - autoFocus?: bool
 *  - testIdPrefix?: string (default 'place-autocomplete')
 *
 * Behaviour notes:
 *  - Uses a single session_token (UUID) per autocomplete-then-details session
 *    for Google's billing-efficient mode. Resets after each selection.
 *  - 300ms debounce on keystrokes; minimum 2 chars before firing.
 *  - "Use current location" runs Geolocation + reverse-geocodes via /nearby.
 */
function genSessionToken() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export default function PlaceAutocomplete({
  value,
  onChange,
  accentColor = "#0281FF",
  placeholder = "Search any place: Costco, Hacienda Real, your office…",
  autoFocus = false,
  testIdPrefix = "place-autocomplete",
}) {
  const [configured, setConfigured] = useState(null);   // null=loading
  const [query, setQuery] = useState(value?.name || value?.address || "");
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sessionToken, setSessionToken] = useState(() => genSessionToken());
  const [coords, setCoords] = useState(null);  // optional user-provided lat/lng
  const [resolving, setResolving] = useState(false); // "use my location" spinner
  const [manualMode, setManualMode] = useState(false); // fallback typing
  const containerRef = useRef(null);
  const debounceRef = useRef(null);
  const activeRequestRef = useRef(0);

  // -------- Probe backend config on first mount --------
  useEffect(() => {
    let cancelled = false;
    api
      .get("/places/status")
      .then(({ data }) => {
        if (!cancelled) setConfigured(!!data?.configured);
      })
      .catch(() => {
        if (!cancelled) setConfigured(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Re-sync the displayed text whenever the parent value changes (e.g. editing existing task)
  useEffect(() => {
    if (value?.name || value?.address) {
      setQuery(value.name || value.address || "");
    }
  }, [value?.name, value?.address]);

  // -------- Click-outside to close --------
  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // -------- Debounced fetch --------
  const fetchSuggestions = useCallback(
    async (input) => {
      if (!input || input.trim().length < 2) {
        setSuggestions([]);
        setLoading(false);
        return;
      }
      const requestId = ++activeRequestRef.current;
      setLoading(true);
      setError("");
      try {
        const params = { input, sessionToken };
        if (coords) {
          params.lat = coords.lat;
          params.lng = coords.lng;
        }
        const { data } = await api.get("/places/autocomplete", { params });
        if (requestId !== activeRequestRef.current) return; // outdated
        setSuggestions(data || []);
      } catch (e) {
        if (requestId !== activeRequestRef.current) return;
        const status = e.response?.status;
        if (status === 503) {
          // Backend not configured — flip to manual mode permanently
          setConfigured(false);
          setManualMode(true);
        } else {
          setError(formatApiError(e.response?.data?.detail) || "Couldn't search places");
        }
        setSuggestions([]);
      } finally {
        if (requestId === activeRequestRef.current) setLoading(false);
      }
    },
    [coords, sessionToken]
  );

  useEffect(() => {
    if (configured === false) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(query), 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, fetchSuggestions, configured]);

  // -------- Selection --------
  const handleSelect = async (suggestion) => {
    setShowDropdown(false);
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/places/details", {
        params: { place_id: suggestion.place_id, sessionToken },
      });
      onChange({
        ...(value || {}),
        place_id: data.place_id,
        name: data.name || suggestion.main_text,
        address: data.formatted_address,
        latitude: data.latitude,
        longitude: data.longitude,
        radius_meters: value?.radius_meters || 200,
      });
      setQuery(data.name || data.formatted_address);
      toast.success(`Pinned: ${data.name || suggestion.main_text}`);
    } catch (e) {
      const detail = formatApiError(e.response?.data?.detail);
      toast.error(detail || "Couldn't load place details");
    } finally {
      setLoading(false);
      // Rotate session token (Google billing best-practice)
      setSessionToken(genSessionToken());
    }
  };

  // -------- "Use current location" --------
  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation isn't available in this browser");
      return;
    }
    setResolving(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setCoords({ lat, lng });
        // Try reverse-resolve to a place via nearby search (works only with key)
        try {
          if (configured) {
            const { data } = await api.get("/places/nearby", {
              params: { lat, lng, radius: 100 },
            });
            const first = (data || [])[0];
            if (first) {
              onChange({
                ...(value || {}),
                place_id: first.place_id,
                name: first.name || "Current location",
                address: first.formatted_address,
                latitude: first.latitude ?? lat,
                longitude: first.longitude ?? lng,
                radius_meters: value?.radius_meters || 200,
              });
              setQuery(first.name || "Current location");
              toast.success(`Pinned: ${first.name || "Current location"}`);
              setResolving(false);
              return;
            }
          }
        } catch {
          // fall through to bare-coords
        }
        // Fallback: just store the raw coordinates
        onChange({
          ...(value || {}),
          name: value?.name || "Current location",
          latitude: lat,
          longitude: lng,
          radius_meters: value?.radius_meters || 200,
        });
        setQuery(value?.name || "Current location");
        toast.success("Pinned your current location");
        setResolving(false);
      },
      (err) => {
        setResolving(false);
        toast.error(err.message || "Couldn't get your location");
      },
      { enableHighAccuracy: true, timeout: 10_000 }
    );
  };

  // -------- Clear selection --------
  const clearSelection = () => {
    setQuery("");
    onChange({
      ...(value || {}),
      place_id: null,
      name: "",
      address: null,
      latitude: null,
      longitude: null,
    });
    setSuggestions([]);
    setShowDropdown(false);
    setSessionToken(genSessionToken());
  };

  const hasSelection = !!(value?.place_id || value?.latitude || value?.address);
  const isConfigured = configured !== false && !manualMode;

  return (
    <div
      ref={containerRef}
      className="relative"
      data-testid={`${testIdPrefix}-root`}
    >
      <div className="flex gap-2 items-stretch">
        <div className="flex-1 relative">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
          />
          <Input
            data-testid={`${testIdPrefix}-input`}
            autoFocus={autoFocus}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setShowDropdown(true);
            }}
            onFocus={() => setShowDropdown(true)}
            placeholder={
              isConfigured
                ? placeholder
                : "Type any address (Place search is offline)"
            }
            className="pl-9 pr-9 h-10"
          />
          {query && (
            <button
              type="button"
              onClick={clearSelection}
              data-testid={`${testIdPrefix}-clear-btn`}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-muted-foreground hover:text-foreground hover:bg-secondary"
              aria-label="Clear"
            >
              <X size={13} />
            </button>
          )}
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={useCurrentLocation}
          disabled={resolving}
          data-testid={`${testIdPrefix}-current-location-btn`}
          className="shrink-0 h-10"
          title="Use my current location"
        >
          {resolving ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Crosshair size={14} />
          )}
        </Button>
      </div>

      {/* Dropdown */}
      {isConfigured && showDropdown && (loading || suggestions.length > 0 || error) && (
        <ul
          data-testid={`${testIdPrefix}-dropdown`}
          className="absolute z-50 mt-1 w-full max-h-72 overflow-y-auto rounded-lg border border-border bg-popover shadow-2xl"
        >
          {loading && (
            <li
              className="px-3 py-2.5 flex items-center gap-2 text-xs text-muted-foreground"
              data-testid={`${testIdPrefix}-loading`}
            >
              <Loader2 size={12} className="animate-spin" />
              Searching…
            </li>
          )}
          {!loading && error && (
            <li
              className="px-3 py-2.5 flex items-center gap-2 text-xs text-red-500"
              data-testid={`${testIdPrefix}-error`}
            >
              <AlertCircle size={12} /> {error}
            </li>
          )}
          {!loading && !error && suggestions.length === 0 && query.length >= 2 && (
            <li className="px-3 py-2.5 text-xs text-muted-foreground">
              No matches. Try a longer name or address.
            </li>
          )}
          {suggestions.map((s) => (
            <li key={s.place_id}>
              <button
                type="button"
                onClick={() => handleSelect(s)}
                data-testid={`${testIdPrefix}-suggestion-${s.place_id}`}
                className="w-full text-left px-3 py-2 flex items-start gap-2 hover:bg-secondary/60 transition-colors"
              >
                <MapPin
                  size={13}
                  className="mt-0.5 shrink-0"
                  style={{ color: accentColor }}
                  strokeWidth={1.8}
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">
                    {s.main_text}
                  </div>
                  {s.secondary_text && (
                    <div className="text-[11px] text-muted-foreground truncate">
                      {s.secondary_text}
                    </div>
                  )}
                </div>
              </button>
            </li>
          ))}
          {/* Manual fallback footer */}
          <li
            className="border-t border-border px-3 py-2 bg-secondary/30 text-[11px] text-muted-foreground flex items-center gap-1.5"
          >
            <Pencil size={11} /> Can't find it?
            <button
              type="button"
              onClick={() => {
                setManualMode(true);
                setShowDropdown(false);
              }}
              data-testid={`${testIdPrefix}-manual-btn`}
              className="text-foreground underline hover:no-underline"
            >
              Enter address manually
            </button>
          </li>
        </ul>
      )}

      {/* Manual mode: surface address + lat/lng inputs */}
      {(!isConfigured || manualMode) && (
        <div
          data-testid={`${testIdPrefix}-manual-fields`}
          className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2"
        >
          <Input
            data-testid={`${testIdPrefix}-manual-name`}
            placeholder="Name (e.g. Home, Office)"
            value={value?.name || ""}
            onChange={(e) => onChange({ ...(value || {}), name: e.target.value })}
            className="text-sm"
          />
          <Input
            data-testid={`${testIdPrefix}-manual-address`}
            placeholder="Street, City"
            value={value?.address || ""}
            onChange={(e) => onChange({ ...(value || {}), address: e.target.value })}
            className="text-sm"
          />
          <Input
            data-testid={`${testIdPrefix}-manual-lat`}
            placeholder="Latitude (optional)"
            type="number"
            step="0.000001"
            value={value?.latitude ?? ""}
            onChange={(e) =>
              onChange({
                ...(value || {}),
                latitude: e.target.value ? parseFloat(e.target.value) : null,
              })
            }
            className="font-mono text-xs"
          />
          <Input
            data-testid={`${testIdPrefix}-manual-lng`}
            placeholder="Longitude (optional)"
            type="number"
            step="0.000001"
            value={value?.longitude ?? ""}
            onChange={(e) =>
              onChange({
                ...(value || {}),
                longitude: e.target.value ? parseFloat(e.target.value) : null,
              })
            }
            className="font-mono text-xs"
          />
        </div>
      )}

      {/* Selected place preview */}
      {hasSelection && !manualMode && isConfigured && (
        <div
          data-testid={`${testIdPrefix}-selected`}
          className="mt-2 rounded-md border border-border bg-secondary/30 px-3 py-2 flex items-start gap-2"
        >
          <MapPin
            size={14}
            style={{ color: accentColor }}
            className="mt-0.5 shrink-0"
            strokeWidth={1.8}
          />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium truncate">{value.name}</div>
            {value.address && (
              <div className="text-[11px] text-muted-foreground truncate">
                {value.address}
              </div>
            )}
            {value.latitude && value.longitude && (
              <div className="text-[10px] font-mono text-muted-foreground/60 mt-0.5">
                {Number(value.latitude).toFixed(5)},{" "}
                {Number(value.longitude).toFixed(5)}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => setManualMode(true)}
            className="text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground"
            data-testid={`${testIdPrefix}-edit-manual-btn`}
          >
            Edit
          </button>
        </div>
      )}
    </div>
  );
}
